import React, { useState, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Button,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemText,
    FormHelperText,
    Chip,
} from '@mui/material';

import { useFormik } from "formik";

import { useNavigate, useParams } from "react-router-dom";

import { useDocuments, documentTypes, targetTypes, actionTypes } from "../../../hooks/api/useDocuments";
import { useInventories, inventoriesTypes } from "../../../hooks/api/useInventories";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemUtils } from "../../../hooks/useSystemUtils";
import { useAppContextFeatures } from "../../../context/AppContext";

// import { CommonIcons, icons } from "../../common/CommonIcons";
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";

import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from '../../../hooks/useTranslations';

import { validationSchema, initialValues } from "../../../schema/documents/retailCashSalesStockPostSchema"

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    // opacity: 0.1,
    //   backgroundColor: "green",
    //   color: 'inherit',
    //   display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

export default function StockPostRetailCashSales({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const { setSystemLoading } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get, stockPost } = useDocuments({ 
        documentType: documentTypes.retailCashSales, 
        targetType: targetTypes.retailCashSales, 
        actionType: actionTypes.retailCashSales.postToStock
    });
    const { list: listInventories, filterOperatorTypes } = useInventories({ inventoriesType: inventoriesTypes.inventory });
    const { formatCurrencyDisplay, currencyAbb } = useSystemUtils();
    const navigate = useNavigate();

    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.retailCashSales.id)

    const [selectedIds, setSelectedIds] = useState([])

    // const [ data, setData ] = useState()
    const [savedValues, setSavedValues] = useState()
    const [dataBatch, setDataBatch] = useState([])

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await get({ id });
            // console.log('response?.data?.items', response?.data)
            // setData(response?.data)

            const {
                document,
                items
            } = response?.data

            const rows = items.map(i => ({
                id: i?.item_id,
                slotId: "",
                docItemId: i?.id,
                customBatchNo: "",
                expiryDate: "",
                code: i?.code,
                itemName: i?.item_name,
                uomName: i?.uom_name,
                itemDesc: i?.description,
                requiredQty: i?.qty,
                isStockPost: i?.is_post_stock === "1",
                inventoryIndexNo: 0
            }))

            setSavedValues(prev => ({
                ...prev,
                documentDate: document?.doc_date,
                documentNumber: document?.doc_number,
                status: document?.is_active_status,
                isStockPost: document?.is_post_stock,
                profileName: document?.profile_name,
                code: document?.code,
                address1: document?.address_1,
                address2: document?.address_2,
                postcode: document?.postcode,
                areaName: document?.area_name,
                country_name: document?.country_name,
                remark: document?.remark,
                userId: document?.user_id,
                rows
            }))

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    const fetchInventoryBatch = async ({ itemIdArray = [] }) => {

        setSystemLoading(true)

        try {

            let props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "rack_name",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "id",
                        operator: filterOperatorTypes.anyOf,
                        value: JSON.stringify(itemIdArray)
                    }
                ],
                dataTypes: ["group_slot_batch"]
            }

            let response = await listInventories(props);

            setSystemLoading(false)

            setDataBatch(response?.data?.inventories ? response?.data?.inventories : [])

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

        return []

    }

    useEffect(() => {

        fetchData();

    }, [])

    const onSubmit = async (currentValues) => {

        setSystemLoading(true);

        const document = {
            doc_date: currentValues?.documentDate,
            // doc_remark: ""
        }

        const profiles = {
            member: {
                user_id: currentValues?.userId
            }
        }

        let items = [];

        currentValues.rows.forEach(e => {

            if (e?.isStockPost === false) { // only is not yet post
                if (e?.slotId) {
                    items.push({
                        id: e?.id,
                        doc_item_id: e?.docItemId,
                        deductions: {
                            slot_id: e?.slotId,
                            custom_batch_no: e?.customBatchNo ? e.customBatchNo : "",
                            expiry_date: e?.expiryDate ? e.expiryDate : ""
                        }
                    })
                }
            }

        })

        try {

            let data = {
                document,
                profiles,
                items
            }

            // check the selection
            if (items.length <= 0) {
                throw new Error(`Please select any item`)
            }

            // console.log('data', data)

            const response = await stockPost({ id, data });

            // console.log('response (outside)', response, `-${response?.warning}`)

            promptMessage(true, `${response?.message}`, response)

            // refresh data
            // fetchData();

            // const itemIdArray = getItemIds()

            // if(itemIdArray && itemIdArray.length)
            //     fetchInventoryBatch({ itemIdArray })


        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false);

    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;


    const getItemIds = () => {

        if (!values) return []

        return values && values?.rows.map(i => i.id)
    }

    useEffect(() => {

        if (dataBatch.length <= 0) {
            const itemIdArray = getItemIds()

            if (itemIdArray && itemIdArray.length)
                fetchInventoryBatch({ itemIdArray })
        }


    }, [values])

    const RenderTable = () => {

        if (!values) return <></>

        const rows = values?.rows;

        return (
            <TableContainer
                // component={Paper}
                sx={{
                    minHeight: 400,
                    maxHeight: 400
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell width="50">#</TableCell>

                            {/* <TableCell align="center" width={120}>Stock Posted</TableCell> */}

                            <TableCell width={200}>{t('code')}</TableCell>
                            <TableCell width={250}>{t('name')}</TableCell>
                            <TableCell>{t('description')}</TableCell>
                            <TableCell align="right" width={150}>{t('requiredQty')}</TableCell>
                            <TableCell align="right" width={250}>{t('availableQty')}</TableCell>
                            <TableCell align="right" width={140}>{t('status')}</TableCell>

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            rows && rows.length > 0
                            && rows.map((item, index) => {

                                const itemValues = item;
                                let itemTouched = touched?.rows
                                let itemErrors = errors?.rows
                                // const itemTouched = touched?.rows

                                if (itemTouched) {
                                    itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                                }

                                if (itemErrors) {
                                    itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                                }


                                return (
                                    <TableRow key={index}>
                                        <TableCell>{(index + 1)}</TableCell>

                                        <TableCell>{itemValues.code}</TableCell>
                                        <TableCell>{itemValues.itemName}</TableCell>
                                        <TableCell>{itemValues.itemDesc}</TableCell>
                                        <TableCell align="right">{parseFloat(itemValues.requiredQty)} {itemValues.uomName}</TableCell>


                                        <TableCell align="right">

                                            <FormControl
                                                margin="dense"
                                                fullWidth
                                                size="small"
                                                error={itemTouched?.inventoryIndexNo && Boolean(itemErrors?.inventoryIndexNo)}
                                            >
                                                <InputLabel id="inventoryIndexNo">{t('inventory')}</InputLabel>
                                                <Select
                                                    // labelId="customValue"
                                                    label={t('inventory')}
                                                    name={`rows.${index}.inventoryIndexNo`}
                                                    onChange={(event) => {
                                                        // console.log('itemValues', itemValues)

                                                        const indexNo = parseInt(event.target.value, 10)

                                                        const selected = dataBatch.filter((i, index3) => index3 === indexNo)

                                                        // console.log('selected', selected, indexNo)

                                                        if (selected.length) {
                                                            setFieldValue(`rows.${index}.inventoryIndexNo`, indexNo)
                                                            setFieldValue(`rows.${index}.expiryDate`, selected[0]?.expiry_date)
                                                            setFieldValue(`rows.${index}.customBatchNo`, selected[0]?.custom_batch_no)
                                                            setFieldValue(`rows.${index}.slotId`, selected[0]?.slot_id)
                                                        }

                                                    }}
                                                    size='small'
                                                    margin='dense'
                                                    value={itemValues?.inventoryIndexNo ? itemValues.inventoryIndexNo : ""}
                                                    disabled={
                                                        itemValues?.isStockPost === true
                                                    }
                                                >
                                                    {
                                                        dataBatch && dataBatch.map((i, index2) => {
                                                            return (
                                                                <MenuItem
                                                                    key={index2}
                                                                    value={index2}
                                                                >
                                                                    <ListItemText
                                                                        primary={`${i.custom_batch_no} (${i.rack_name}/${i.slot_name}) [${i.total_remain_qty}]`}
                                                                    />
                                                                </MenuItem>
                                                            )
                                                        })
                                                    }
                                                </Select>
                                                <FormHelperText>
                                                    {itemTouched?.inventoryIndexNo && itemErrors?.inventoryIndexNo}
                                                </FormHelperText>
                                            </FormControl>

                                        </TableCell>

                                        <TableCell align="right">
                                            {
                                                itemValues?.isStockPost ?
                                                    (
                                                        <Chip
                                                            label={t(`stockPosted`)}
                                                            variant="contained"
                                                            color={"primary"}
                                                        />
                                                    )
                                                    :
                                                    (
                                                        <Chip
                                                            label={t(`pending`)}
                                                            variant="outlined"
                                                            color={"secondary"}
                                                        />
                                                    )
                                            }
                                        </TableCell>

                                    </TableRow>
                                )
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const RenderFormProfile = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`relatedProfilesAttachedToDocument`)}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('orderedBy')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.profileName} ({values?.code})
                                </StyledContent>
                                <StyledContent2>
                                    {values?.address1} {values?.address2} {values?.postcode}, {values?.areaName} {values?.countryName}
                                </StyledContent2>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.documentNumber}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentDate')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.documentDate}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('status')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.status === "1" ? 'Active' : 'Cancelled'}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('stockPosted')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.isStockPost === "1" ? 'Completed' : 'No'}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('remark')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {values?.remark}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {
        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        {
                            RenderFormProfile()
                        }

                    </Grid>
                </Grid>

                <Box
                    sx={{
                        textAlign: 'left',
                        mt: 3,
                        pl: 0.5,
                    }}
                >
                    <CommonSectionHeader
                        title={t(`items`)}
                        subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                    />
                </Box>

                <form onSubmit={handleSubmit}>


                    <StyledCard
                        sx={{
                            mt: 1.5,
                            p: 0
                        }}
                    >
                        <Grid container rowSpacing={0}>

                            <RenderTable />

                        </Grid>
                    </StyledCard>
                    <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                        <Grid item md={12} textAlign={"right"}>
                            <Button
                                type="submit"
                                // fullWidth
                                variant="contained"
                                sx={{
                                    mt: 3,
                                    mb: 2
                                }}
                            // loading={isLoading}
                            // onClick={() => {

                            //     if(selectedIds.length <= 0) {
                            //         promptMessage(false, `Please select at least (1) item`)
                            //     }else{
                            //         handleSubmit()
                            //     }

                            // }}
                            >
                                {t('saveChanges')}
                            </Button>
                        </Grid>
                    </Grid>

                </form>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.stockRetailCashSales })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={`${t('stockPost')} ${values?.documentNumber}`}
                breadcrumbs={[
                    {
                        displayText: t("retailCashSales"),
                        props: {
                            // underline: "hover",
                            href: getRoute('documents/retailCashSales')
                        }
                    },
                    {
                        displayText: `#${values?.documentNumber}`,
                        props: {
                            // disabled: true,
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageStockPost.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}